import React, { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button, ButtonGroup, Table } from 'react-bootstrap';
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { getRecordEfforts } from '../api/trainer';
import { EFFORT_LIMITS, EffortLimit, RecordDistance, RecordEffortsResponse } from '../api/types';
import { statusOf } from '../api/errors';
import ChartCard from '../components/ChartCard';
import PageHeader from '../components/PageHeader';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import { SOURCE_LABELS, formatDate, formatDistance, formatDuration, formatPace, shortDate } from '../utils/format';
import { isRecordCode } from '../utils/records';
import { Y_AXIS, yAxisProps } from '../utils/axis';

const DEFAULT_LIMIT: EffortLimit = 50;
const AXIS = { stroke: 'var(--text-muted)', fontSize: 12 };
const MARGIN = { top: 8, right: 8, bottom: 0, left: 0 };

function limitFromParams(params: URLSearchParams): EffortLimit {
  const value = Number(params.get('limit'));
  return (EFFORT_LIMITS as readonly number[]).includes(value) ? (value as EffortLimit) : DEFAULT_LIMIT;
}

/** Route page for /records/:distance. Unknown codes get a friendly 404 without a request. */
const RecordEffortsPage: React.FC = () => {
  const { distance = '' } = useParams();
  if (!isRecordCode(distance)) {
    return (
      <>
        <BackLink />
        <PageHeader title="Record not found">
          <p>
            There is no record called &ldquo;{distance}&rdquo;. <Link to="/records">Back to Records</Link>
          </p>
        </PageHeader>
      </>
    );
  }
  return <RecordEfforts distance={distance} />;
};

const BackLink: React.FC = () => (
  <Link to="/records" className="small d-inline-block mb-1">
    <span aria-hidden="true">‹</span> Records
  </Link>
);

type EffortsLoad = { missing: true } | { missing: false; data: RecordEffortsResponse };

const RecordEfforts: React.FC<{ distance: RecordDistance }> = ({ distance }) => {
  const [params, setParams] = useSearchParams();
  const limit = limitFromParams(params);

  // A 404 from the server means the code is unknown there too. Other failures go to the shared error block.
  const efforts = useAsync<EffortsLoad>(async () => {
    try {
      return { missing: false, data: await getRecordEfforts(distance, limit) };
    } catch (err) {
      if (statusOf(err) === 404) return { missing: true };
      throw err;
    }
  }, [distance, limit]);

  const loaded = efforts.data && !efforts.data.missing ? efforts.data.data : null;
  const chartRows = useMemo(
    () =>
      loaded
        ? [...loaded.items]
            .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))
            .map(item => ({ date: shortDate(item.date), fullDate: item.date, timeS: item.timeS, name: item.activityName }))
        : [],
    [loaded]
  );

  if (efforts.data?.missing) {
    return (
      <>
        <BackLink />
        <PageHeader title="Record not found">
          <p>
            There is no record called &ldquo;{distance}&rdquo;. <Link to="/records">Back to Records</Link>
          </p>
        </PageHeader>
      </>
    );
  }

  const best = loaded?.items[0];
  const override = loaded?.override ?? null;
  const current = override
    ? { timeS: override.timeS, source: override.source as 'manual' | 'auto' | 'adjusted', date: override.date, activityId: override.activityId }
    : best
      ? { timeS: best.timeS, source: best.source, date: best.date, activityId: best.activityId }
      : null;

  const selectLimit = (value: EffortLimit) => setParams({ limit: String(value) });

  return (
    <>
      <BackLink />
      <PageHeader title={loaded?.name ?? 'Record'}>
        <p>Best runs at this distance, running activities only, fastest first.</p>
      </PageHeader>

      <StatusMessage loading={efforts.loading} error={efforts.error} onRetry={efforts.reload} />

      {loaded && (
        <>
          <section className="surface mb-3">
            <div className="stat-label">Current record</div>
            {current ? (
              <>
                <div className="stat-value tabular">{formatDuration(current.timeS)}</div>
                <div className="stat-hint d-flex flex-wrap align-items-center gap-2">
                  <span className={`source-badge source-${current.source}`}>{SOURCE_LABELS[current.source] ?? current.source}</span>
                  {current.date && <span>{formatDate(current.date)}</span>}
                  {current.source !== 'manual' && current.activityId != null && (
                    <Link to={`/activities/${current.activityId}`}>View activity</Link>
                  )}
                </div>
              </>
            ) : (
              <p className="muted mb-0">No run covers {loaded.name.toLowerCase()} yet.</p>
            )}

            {override && (
              <div className="mt-3 small">
                <div>
                  Manual override: <span className="tabular fw-semibold">{formatDuration(override.timeS)}</span>
                  {override.note ? ` · ${override.note}` : ''}
                </div>
                {best && best.timeS !== override.timeS && (
                  <div className="muted">Best computed run: {formatDuration(best.timeS)} on {formatDate(best.date)}</div>
                )}
              </div>
            )}
          </section>

          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <ButtonGroup size="sm" aria-label="Number of runs">
              {EFFORT_LIMITS.map(n => (
                <Button
                  key={n}
                  variant={n === limit ? 'primary' : 'outline-secondary'}
                  aria-pressed={n === limit}
                  onClick={() => selectLimit(n)}
                >
                  {n}
                </Button>
              ))}
            </ButtonGroup>
            <span className="small muted">
              {loaded.items.length === 0
                ? 'No runs'
                : `Fastest ${loaded.items.length} of up to ${loaded.limit} runs`}
            </span>
          </div>

          <div className="mb-3">
            <ChartCard title="Times by date (lower is faster)" empty={chartRows.length === 0}>
              <LineChart data={chartRows} margin={MARGIN}>
                <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                <YAxis {...Y_AXIS} {...yAxisProps(chartRows, ['timeS'], { kind: 'time' })} tickFormatter={v => formatDuration(Number(v))} width={64} />
                <Tooltip
                  labelFormatter={(_, payload) => formatDate(payload?.[0]?.payload?.fullDate)}
                  formatter={(value: number) => [formatDuration(value), 'Time']}
                />
                <Line type="monotone" dataKey="timeS" stroke="var(--chart-1)" strokeWidth={2} dot />
              </LineChart>
            </ChartCard>
          </div>

          <section className="surface">
            {loaded.items.length === 0 ? (
              <div className="history-empty">
                <p className="fw-semibold mb-1">No runs yet</p>
                <p className="muted mb-0">
                  No running activity reaches {loaded.name.toLowerCase()} ({formatDistance(loaded.meters)}). Runs
                  appear here once Garmin has synced them.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table size="sm" className="trainer-table">
                  <thead>
                    <tr>
                      <th className="text-end">Rank</th>
                      <th>Date</th>
                      <th>Activity</th>
                      <th className="text-end">Time</th>
                      <th className="text-end">Pace</th>
                      <th>Source</th>
                      <th className="text-end">Activity distance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loaded.items.map((item, index) => (
                      <tr key={`${item.activityId}-${item.timeS}`}>
                        <td className="text-end tabular">{index + 1}</td>
                        <td className="tabular">{formatDate(item.date)}</td>
                        <td>
                          <Link to={`/activities/${item.activityId}`}>{item.activityName ?? 'Untitled activity'}</Link>
                        </td>
                        <td className="text-end tabular fw-semibold">{formatDuration(item.timeS)}</td>
                        <td className="text-end tabular">{formatPace(item.paceSPerKm)}</td>
                        <td>
                          <span className={`source-badge source-${item.source}`}>{SOURCE_LABELS[item.source]}</span>
                        </td>
                        <td className="text-end tabular">{formatDistance(item.activityDistanceM)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
};

export default RecordEffortsPage;
